import { db } from "@/lib/firebase";
import { router } from "expo-router";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import {
  getAdminBusinessCategoryLabel,
  getAdminBusinessSubcategoryLabel,
  listenBusinessesAdmin,
  normalizeAdminBusiness,
  shouldShowAdminOwnerSuggestedLabel,
  type AdminBusiness,
  type AdminBusinessStatus,
} from "@/lib/admin/businessesAdmin";

const STATUS: ("all" | AdminBusinessStatus)[] = [
  "all",
  "draft",
  "pending",
  "approved",
  "rejected",
  "changes_requested",
];

type AdminBusinessRow = AdminBusiness & {
  accountNumber?: string;
  accountNumberCreatedAt?: unknown;

  ownerName?: string;
  ownerPhone?: string;

  phone?: string;
  email?: string;
  businessEmail?: string;
};

function cleanText(value: unknown) {
  if (typeof value !== "string") return "";
  return value.trim();
}

function normalizeAccountNumber(value: unknown) {
  return cleanText(value).replace(/\D/g, "");
}

function includesText(value: unknown, search: string) {
  return cleanText(value).toLowerCase().includes(search);
}

export default function AdminBusinessesScreen() {
  const [status, setStatus] = useState<(typeof STATUS)[number]>("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState<AdminBusinessRow[]>([]);
  const [accountSearchRows, setAccountSearchRows] = useState<
    AdminBusinessRow[] | null
  >(null);
  const [searchingAccount, setSearchingAccount] = useState(false);

  useEffect(() => {
    const unsub = listenBusinessesAdmin(
      { status, showDeleted, limitCount: 80 },
      (nextRows) => setRows(nextRows as AdminBusinessRow[])
    );

    return () => unsub();
  }, [status, showDeleted]);

  const searchText = search.trim().toLowerCase();
  const searchAccountNumber = normalizeAccountNumber(search);

  useEffect(() => {
    let cancelled = false;

    async function searchExactAccountNumber() {
      if (searchAccountNumber.length !== 11) {
        setAccountSearchRows(null);
        setSearchingAccount(false);
        return;
      }

      setSearchingAccount(true);

      try {
        const q = query(
          collection(db, "businesses"),
          where("accountNumber", "==", searchAccountNumber),
          limit(10)
        );

        const snap = await getDocs(q);

        if (cancelled) return;

        const foundRows = snap.docs.map((docSnap) =>
          normalizeAdminBusiness(docSnap.data(), docSnap.id)
        ) as AdminBusinessRow[];

        setAccountSearchRows(foundRows);
      } catch {
        console.warn("Admin account number search failed.");

        if (!cancelled) {
          setAccountSearchRows([]);
        }
      } finally {
        if (!cancelled) {
          setSearchingAccount(false);
        }
      }
    }

    void searchExactAccountNumber();

    return () => {
      cancelled = true;
    };
  }, [searchAccountNumber]);

  const filtered = useMemo(() => {
    if (accountSearchRows) {
      return accountSearchRows;
    }

    if (searchText.length < 2 && searchAccountNumber.length < 2) {
      return rows;
    }

    return rows.filter((business) => {
      const accountNumber = normalizeAccountNumber(business.accountNumber);

      const matchesAccountNumber =
        searchAccountNumber.length >= 2 &&
        accountNumber.includes(searchAccountNumber);

      const matchesText =
        searchText.length >= 2 &&
        (includesText(business.name, searchText) ||
          includesText(business.slug, searchText) ||
          includesText(business.ownerName, searchText) ||
          includesText(business.ownerPhone, searchText) ||
          includesText(business.phone, searchText) ||
          includesText(business.email, searchText) ||
          includesText(business.businessEmail, searchText) ||
          includesText(getAdminBusinessCategoryLabel(business), searchText) ||
          includesText(getAdminBusinessSubcategoryLabel(business), searchText));

      return matchesAccountNumber || matchesText;
    });
  }, [accountSearchRows, rows, searchAccountNumber, searchText]);

  return (
    <SafeAreaView edges={["top"]} style={styles.safe}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.push("/admin" as any)}
            style={styles.backButton}
          >
            <Text style={styles.backButtonText}>← Admin Dashboard</Text>
          </Pressable>

          <Text style={styles.title}>Admin · Businesses</Text>
        </View>

        <View style={styles.moderationRow}>
          <Pressable
            style={styles.moderationCard}
            onPress={() => router.push("/admin/moderation/triage")}
          >
            <Text style={styles.moderationTitle}>Moderate Pending</Text>
            <Text style={styles.moderationSub}>
              Fast approve / reject queue
            </Text>
          </Pressable>

          <Pressable
            style={styles.moderationCard}
            onPress={() =>
              router.push({
                pathname: "/admin/moderation/triage",
                params: { status: "changes_requested" },
              })
            }
          >
            <Text style={styles.moderationTitle}>Changes Requested</Text>
            <Text style={styles.moderationSub}>Review updated businesses</Text>
          </Pressable>
        </View>

        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search account number, name, slug, phone, or email…"
          keyboardType="default"
          autoCapitalize="none"
          style={styles.search}
        />

        {searchingAccount ? (
          <Text style={styles.searchHint}>Searching account number...</Text>
        ) : searchAccountNumber.length > 0 && searchAccountNumber.length < 11 ? (
          <Text style={styles.searchHint}>
            Account number should be 11 digits.
          </Text>
        ) : null}

        <View style={styles.filtersRow}>
          {STATUS.map((s) => (
            <Pressable
              key={s}
              onPress={() => setStatus(s)}
              style={[styles.pill, status === s && styles.pillActive]}
            >
              <Text
                style={[
                  styles.pillText,
                  status === s && styles.pillTextActive,
                ]}
              >
                {s}
              </Text>
            </Pressable>
          ))}

          <Pressable
            onPress={() => setShowDeleted((v) => !v)}
            style={[styles.pill, showDeleted && styles.pillActive]}
          >
            <Text
              style={[styles.pillText, showDeleted && styles.pillTextActive]}
            >
              {showDeleted ? "Showing deleted" : "Hide deleted"}
            </Text>
          </Pressable>
        </View>

        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          renderItem={({ item }) => {
            const accountNumber = normalizeAccountNumber(item.accountNumber);
            const categoryText = getAdminBusinessCategoryLabel(item);
            const subcategoryText = getAdminBusinessSubcategoryLabel(item);
            const categoryLabel = categoryText || "Not set";
            const subcategoryLabel = subcategoryText || "Not set";
            const showCategoryOwnerSuggested = shouldShowAdminOwnerSuggestedLabel(
              categoryText,
              item.categoryStatus,
              item.isCategoryOwnerSuggested
            );
            const showSubcategoryOwnerSuggested =
              shouldShowAdminOwnerSuggestedLabel(
                subcategoryText,
                item.subcategoryStatus,
                item.isSubcategoryOwnerSuggested
              );

            return (
              <Pressable
                style={styles.itemCard}
                onPress={() =>
                  router.push({
                    pathname: "/admin/businesses/[id]",
                    params: { id: item.id },
                  })
                }
              >
                <View style={{ flex: 1 }}>
                  <Text style={styles.itemTitle}>
                    {item.name || "Unnamed business"}
                  </Text>

                  <Text style={styles.accountNumber}>
                    Account No: {accountNumber || "Not assigned"}
                  </Text>

                  <Text style={styles.itemSub}>/{item.slug || "no-slug"}</Text>

                  <View style={styles.taxonomyWrap}>
                    <View style={styles.taxonomyRow}>
                      <Text style={styles.itemTaxonomy} numberOfLines={1}>
                        Category: {categoryLabel}
                      </Text>
                      {showCategoryOwnerSuggested ? (
                        <View style={styles.ownerSuggestedBadge}>
                          <Text style={styles.ownerSuggestedText}>
                            Owner suggested
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    <View style={styles.taxonomyRow}>
                      <Text style={styles.itemTaxonomy} numberOfLines={1}>
                        Subcategory: {subcategoryLabel}
                      </Text>
                      {showSubcategoryOwnerSuggested ? (
                        <View style={styles.ownerSuggestedBadge}>
                          <Text style={styles.ownerSuggestedText}>
                            Owner suggested
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </View>

                  <Text style={styles.itemMeta}>
                    Status: {item.status ?? "unknown"} · Published:{" "}
                    {item.isPublished ? "Yes" : "No"}
                    {item.isDeleted ? " · DELETED" : ""}
                    {item.isFeatured ? " · ⭐ Featured" : ""}
                  </Text>
                </View>

                <Text style={styles.openHint}>›</Text>
              </Pressable>
            );
          }}
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <Text style={styles.emptyText}>
                No businesses found for this filter.
              </Text>
            </View>
          }
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: "#fff",
  },

  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 10,
    backgroundColor: "#fff",
  },

  header: {
    marginBottom: 12,
  },

  backButton: {
    alignSelf: "flex-start",
    marginBottom: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: "#F3F4F6",
  },

  backButtonText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },

  title: {
    fontSize: 22,
    fontWeight: "900",
  },

  moderationRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 12,
  },

  moderationCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 16,
    padding: 12,
  },

  moderationTitle: {
    fontWeight: "900",
  },

  moderationSub: {
    opacity: 0.7,
    fontSize: 12,
    marginTop: 4,
  },

  search: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  searchHint: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "700",
    opacity: 0.65,
  },

  filtersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 12,
    marginBottom: 6,
  },

  pill: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },

  pillActive: {
    borderColor: "#111",
  },

  pillText: {
    fontWeight: "800",
    opacity: 0.75,
  },

  pillTextActive: {
    opacity: 1,
  },

  listContent: {
    paddingVertical: 10,
    paddingBottom: 36,
  },

  itemCard: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 16,
    padding: 14,
    marginBottom: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },

  itemTitle: {
    fontSize: 16,
    fontWeight: "900",
  },

  accountNumber: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },

  itemSub: {
    marginTop: 2,
    opacity: 0.65,
  },

  taxonomyWrap: {
    marginTop: 8,
    gap: 4,
  },

  taxonomyRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },

  itemTaxonomy: {
    flexShrink: 1,
    fontSize: 12,
    fontWeight: "800",
    color: "#374151",
  },

  ownerSuggestedBadge: {
    borderWidth: 1,
    borderColor: "#D97706",
    backgroundColor: "#FFFBEB",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },

  ownerSuggestedText: {
    color: "#92400E",
    fontSize: 10,
    fontWeight: "900",
    textTransform: "uppercase",
  },

  itemMeta: {
    marginTop: 8,
    opacity: 0.75,
  },

  openHint: {
    fontSize: 24,
    fontWeight: "900",
    opacity: 0.4,
  },

  emptyWrap: {
    paddingTop: 30,
  },

  emptyText: {
    opacity: 0.7,
  },
});
